import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { DataModeService } from '../services/data-mode.service';
import { RUNTIME_CONFIG, isLocalDemo } from '../configuration/config';
import { normalizeReturnUrl } from './return-url';

export const LOCAL_SESSION_KEY = 'dkc-local-session';
const APPLICATION_ROLES = ['Administrator', 'ProjectManager', 'DevOps', 'Viewer'];
interface Principal {
  id: string;
  name: string | null;
  roles: string[];
  developmentAuthentication: boolean;
}
class SignInFailure extends Error {}
class SignedOut extends SignInFailure {}

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly config = inject(RUNTIME_CONFIG);
  readonly dataMode = inject(DataModeService);
  private readonly router = inject(Router);
  readonly demo = isLocalDemo(this.config);
  readonly id = signal('');
  readonly name = signal(this.demo ? 'Local Administrator' : 'Administrator');
  readonly authenticated = signal(false);
  readonly roles = signal<string[]>([]);
  readonly error = signal('');
  readonly signingIn = signal(false);
  readonly configurationLogin = signal(false);
  private csrf = '';
  private client: PublicClientApplication | null = null;
  private localSession = false;

  get configured(): boolean {
    return (
      !!this.config.entra.tenantId && !!this.config.entra.clientId && !!this.config.entra.apiScope
    );
  }
  get canEdit(): boolean {
    return !this.dataMode.isDemoMode() && this.canManage;
  }
  get canManage(): boolean {
    return this.roles().some((role) => role === 'Administrator' || role === 'ProjectManager');
  }

  get canManageOperations(): boolean {
    return this.canManage || this.roles().includes('DevOps');
  }
  get canProvision(): boolean {
    return this.roles().some((role) => role === 'Administrator' || role === 'DevOps');
  }

  async initialize(): Promise<void> {
    this.clearIdentity();
    this.error.set('');
    try {
      const settings = await fetch(`${this.config.apiBaseUrl}/auth/configuration`, {
        cache: 'no-store',
        credentials: 'include',
      });
      if (settings.ok) {
        const configuration = await settings.json();
        this.configurationLogin.set(configuration.mode === 'configuration');
      }
      if (this.configurationLogin()) {
        this.acceptPrincipal(await this.readPrincipal());
        return;
      }
      if (this.demo && sessionStorage.getItem(LOCAL_SESSION_KEY) === 'active') {
        const principal = await this.readPrincipal();
        this.acceptLocalPrincipal(principal);
        return;
      }
      if (!this.configured) return;
      const client = await this.microsoftClient();
      const result = await client.handleRedirectPromise();
      const accounts = client.getAllAccounts();
      const account =
        result?.account ??
        client.getActiveAccount() ??
        (accounts.length === 1 ? accounts[0] : null);
      if (!account) return;
      client.setActiveAccount(account);
      const token = await this.token();
      if (!token)
        throw new SignInFailure('Your Microsoft session could not be verified. Sign in again.');
      this.acceptPrincipal(await this.readPrincipal(token));
    } catch (error) {
      sessionStorage.removeItem(LOCAL_SESSION_KEY);
      this.clearIdentity();
      if (!(error instanceof SignedOut)) this.error.set(this.signInError(error));
    }
  }

  async signIn(returnUrl?: string): Promise<void> {
    if (this.signingIn()) return;
    this.error.set('');
    if (!this.configured) {
      this.error.set('Microsoft sign-in has not been configured. Contact your administrator.');
      return;
    }
    this.signingIn.set(true);
    try {
      const client = await this.microsoftClient();
      // MSAL returns here after processing the token at the registered origin callback.
      await client.loginRedirect({
        scopes: [this.config.entra.apiScope],
        redirectStartPage: `${window.location.origin}${normalizeReturnUrl(returnUrl)}`,
        prompt: 'select_account',
      });
      // A successful redirect leaves this document. Keep the control disabled until navigation.
    } catch (error) {
      this.error.set(this.signInError(error));
      this.signingIn.set(false);
    }
  }

  async signInLocally(returnUrl?: string): Promise<void> {
    if (this.signingIn()) return;
    this.error.set('');
    if (!this.demo) {
      this.error.set(
        'Local development access is unavailable for this portal. Use Microsoft sign-in.',
      );
      return;
    }
    this.signingIn.set(true);
    try {
      this.acceptLocalPrincipal(await this.readPrincipal());
      sessionStorage.setItem(LOCAL_SESSION_KEY, 'active');
      await this.dataMode.initialize(this.id());
      await this.router.navigateByUrl(normalizeReturnUrl(returnUrl));
    } catch (error) {
      sessionStorage.removeItem(LOCAL_SESSION_KEY);
      this.clearIdentity();
      this.error.set(this.signInError(error));
    } finally {
      this.signingIn.set(false);
    }
  }

  async signInWithPassword(username: string, password: string, returnUrl?: string): Promise<void> {
    if (this.signingIn() || !this.configurationLogin()) return;
    this.signingIn.set(true);
    this.error.set('');
    try {
      this.csrf = '';
      const response = await fetch(`${this.config.apiBaseUrl}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'X-DKC-CSRF': await this.csrfToken(),
          'X-DKC-Data-Mode': 'live',
        },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!response.ok)
        throw new SignInFailure(
          response.status === 401
            ? 'The username or password is incorrect.'
            : response.status === 429
              ? 'Too many sign-in attempts. Wait a minute and try again.'
              : 'Sign-in could not be completed. Refresh and try again.',
        );
      this.csrf = '';
      sessionStorage.removeItem(LOCAL_SESSION_KEY);
      this.acceptPrincipal(await this.readPrincipal());
      await this.dataMode.initialize(this.id());
      await this.router.navigateByUrl(normalizeReturnUrl(returnUrl));
    } catch (error) {
      this.clearIdentity();
      this.error.set(this.signInError(error));
    } finally {
      this.signingIn.set(false);
    }
  }

  async csrfToken(): Promise<string> {
    if (!this.configurationLogin()) return '';
    if (!this.csrf) {
      const response = await fetch(`${this.config.apiBaseUrl}/auth/csrf`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok)
        throw new SignInFailure('Your session could not be verified. Refresh and try again.');
      const body = await response.json();
      if (typeof body.token !== 'string' || !body.token)
        throw new SignInFailure('Your session could not be verified. Refresh and try again.');
      this.csrf = body.token;
    }
    return this.csrf;
  }

  async token(): Promise<string | null> {
    if (this.configurationLogin() || this.localSession || !this.client) return null;
    try {
      const result = await this.client.acquireTokenSilent({ scopes: [this.config.entra.apiScope] });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.client.acquireTokenRedirect({
          scopes: [this.config.entra.apiScope],
          redirectStartPage: `${window.location.origin}${normalizeReturnUrl(window.location.pathname + window.location.search + window.location.hash)}`,
        });
      }
      throw error;
    }
  }

  async signOut(): Promise<void> {
    if (this.configurationLogin()) {
      try {
        const response = await fetch(`${this.config.apiBaseUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: { 'X-DKC-CSRF': await this.csrfToken(), 'X-DKC-Data-Mode': 'live' },
        });
        if (!response.ok && response.status !== 401)
          throw new SignInFailure('Sign-out could not be completed. Try again.');
        this.csrf = '';
        this.clearIdentity();
        sessionStorage.removeItem(LOCAL_SESSION_KEY);
        window.location.assign('/sign-in');
      } catch (error) {
        this.error.set(this.signInError(error));
      }
      return;
    }
    const local = this.localSession;
    sessionStorage.removeItem(LOCAL_SESSION_KEY);
    this.clearIdentity();
    if (local || !this.client) {
      window.location.assign('/sign-in');
      return;
    }
    try {
      await this.client.logoutRedirect();
    } catch {
      await this.client.clearCache();
      window.location.assign('/sign-in');
    }
  }

  private async microsoftClient(): Promise<PublicClientApplication> {
    if (!this.client) {
      const client = new PublicClientApplication({
        auth: {
          clientId: this.config.entra.clientId,
          authority: `https://login.microsoftonline.com/${this.config.entra.tenantId}`,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
        },
        cache: { cacheLocation: 'sessionStorage' },
      });
      try {
        await client.initialize();
      } catch (error) {
        this.client = null;
        throw error;
      }
      this.client = client;
    }
    return this.client;
  }

  private async readPrincipal(token?: string): Promise<Principal> {
    let response: Response;
    try {
      response = await fetch(`${this.config.apiBaseUrl}/auth/me`, {
        cache: 'no-store',
        credentials: 'include',
        headers: {
          'X-DKC-Data-Mode': 'live',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {
      throw new SignInFailure(
        'The operations service is unavailable. Try again when it is reachable.',
      );
    }
    if (response.status === 401) throw new SignedOut('Your session expired. Sign in again.');
    if (response.status === 403)
      throw new SignInFailure(
        'Your account does not have access to this workspace. Contact your administrator.',
      );
    if (!response.ok)
      throw new SignInFailure(
        'Your session could not be verified. Sign in again or check that the operations service is available.',
      );
    const principal = (await response.json()) as Partial<Principal>;
    if (
      typeof principal.id !== 'string' ||
      !principal.id.trim() ||
      !Array.isArray(principal.roles) ||
      !principal.roles.every((role) => typeof role === 'string') ||
      !principal.roles.some((role) => APPLICATION_ROLES.includes(role))
    ) {
      throw new SignInFailure(
        'Your account has not been assigned access to this workspace. Contact your administrator.',
      );
    }
    return {
      id: principal.id,
      name: typeof principal.name === 'string' ? principal.name : null,
      roles: principal.roles.filter((role) => APPLICATION_ROLES.includes(role)),
      developmentAuthentication: principal.developmentAuthentication === true,
    };
  }

  private acceptLocalPrincipal(principal: Principal): void {
    if (!principal.developmentAuthentication) {
      throw new SignInFailure(
        'Local development access is disabled on the API. Use a correctly configured local development environment or Microsoft sign-in.',
      );
    }
    this.localSession = true;
    this.acceptPrincipal(principal);
  }
  private acceptPrincipal(principal: Principal): void {
    this.id.set(principal.id);
    this.name.set(principal.name ?? (this.localSession ? 'Local Administrator' : 'Administrator'));
    this.roles.set(principal.roles);
    this.authenticated.set(true);
  }
  private clearIdentity(): void {
    this.localSession = false;
    this.authenticated.set(false);
    this.roles.set([]);
    this.id.set('');
    this.name.set(this.demo ? 'Local Administrator' : 'Administrator');
  }
  private signInError(error: unknown): string {
    if (error instanceof SignInFailure) return error.message;
    const code =
      typeof error === 'object' && error !== null && 'errorCode' in error ? error.errorCode : null;
    if (code === 'user_cancelled') return 'Sign-in was canceled. Try again when you are ready.';
    if (code === 'interaction_in_progress')
      return 'A Microsoft sign-in is already in progress. Complete it and try again.';
    return 'Sign-in could not be completed. Please try again.';
  }
}
